import React, { useEffect, useMemo } from 'react';
import { Stack, Checkbox, Chip } from '@mui/material';
import { FormSectionHeader } from 'components/shared/FormSectionHeader';
import { AutocompleteField, RadioGroupField } from 'components/form';
import { DealBuilderFormValues, EmailOptionGroups, SendEmailOption } from 'modules/dealBuilder';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCcEmailOptions, useSelectedContactsSummary } from 'modules/dealBuilder/hooks';
import { sendViaOptions } from 'modules/dealBuilder/constants';
import { getContactLabel } from 'modules/dealBuilder/lib';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const renderContactOption = (
  props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
  option: SendEmailOption,
  selected: boolean
) => {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        style={{ marginRight: 8 }}
        checked={selected}
      />
      {getContactLabel(option, true)}
    </li>
  );
};

export const ReviewAndSendFields = () => {
  const { selectedContacts } = useSelectedContactsSummary();
  const { options: ccEmailOptions, defaultCcEmails } = useCcEmailOptions();
  const { control, setValue } = useFormContext<DealBuilderFormValues>();
  const sendEmailTo = useWatch({ control, name: 'reviewSend.sendEmailTo' });

  const sendToOptions = useMemo<SendEmailOption[]>(
    () =>
      selectedContacts
        .filter(c => !!c.email)
        .map(c => ({
          label: c.fullName,
          value: c.email as string,
          fullName: c.fullName,
          email: c.email as string,
          title: c.title ?? '',
          phone: c.phone ?? '',
          group: EmailOptionGroups.CustomerContacts,
        })),
    [selectedContacts]
  );

  useEffect(() => {
    setValue('reviewSend.sendEmailTo', sendToOptions);
  }, [sendToOptions, setValue]);

  useEffect(() => {
    setValue('reviewSend.ccEmails', defaultCcEmails);
  }, [defaultCcEmails, setValue]);

  return (
    <Stack gap={1} direction="row">
      <Stack gap={1} flex={1}>
        <FormSectionHeader name="Send Via" value="sendVia" />
        <RadioGroupField
          row
          control={control}
          className="flexed-radio"
          options={sendViaOptions}
          name="reviewSend.sendVia"
        />
      </Stack>

      <Stack gap={1} flex={1}>
        <FormSectionHeader name="Send To" value="sendTo" />
        <AutocompleteField<SendEmailOption, true, DealBuilderFormValues>
          control={control}
          name="reviewSend.sendEmailTo"
          multiple
          disableCloseOnSelect
          disableClearable={true as any}
          limitTags={3}
          options={sendToOptions}
          getOptionLabel={option => getContactLabel(option)}
          isOptionEqualToValue={(option, value) => !!value && option.email === value.email}
          getOptionDisabled={option => (sendEmailTo ?? []).length === 1 && sendEmailTo![0]?.email === option.email}
          renderOption={(props, option, { selected }) => renderContactOption(props, option, selected)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={getContactLabel(option)}
                  size="small"
                  onDelete={value.length > 1 ? tagProps.onDelete : undefined}
                />
              );
            })
          }
          textFieldProps={{ label: 'Send to', variant: 'outlined', size: 'small' }}
        />
      </Stack>

      <Stack gap={1} flex={1}>
        <FormSectionHeader name="CC Email To" value="ccEmailTo" />
        <AutocompleteField<SendEmailOption, true, DealBuilderFormValues>
          control={control}
          name="reviewSend.ccEmails"
          multiple
          disableCloseOnSelect
          limitTags={3}
          options={ccEmailOptions.sort((a, b) => -b.group.localeCompare(a.group))}
          groupBy={option => option.group}
          getOptionLabel={option => getContactLabel(option)}
          isOptionEqualToValue={(option, value) => !!value && option.email === value.email}
          renderOption={(props, option, { selected }) => renderContactOption(props, option, selected)}
          ChipProps={{ size: 'small' }}
          textFieldProps={{ label: 'CC email to', variant: 'outlined', size: 'small' }}
        />
      </Stack>
    </Stack>
  );
};
